import { db } from '@/services/storage/db';
import { getCurrentUserId } from '@/services/auth/current-user';
import { fetchCurrentUserById } from '@/services/supabase-repository';
import { US_STATES } from '@/utils/us-states';

import type { User, UserPreferences, UpdateUserInput, UpdateUserPreferencesInput } from '@/types/user';

const VALID_STATE_ABBREVS = new Set(US_STATES.map((s) => s.abbrev));

function validateDisplayName(displayName: string): void {
  if (displayName.trim().length === 0) {
    throw new Error('Display name is required');
  }
  if (displayName.trim().length > 100) {
    throw new Error('Display name must be 100 characters or fewer');
  }
}

function validateDefaultState(defaultState: string): void {
  if (defaultState === '') return;
  if (!VALID_STATE_ABBREVS.has(defaultState.toUpperCase())) {
    throw new Error(`Invalid state code: ${defaultState}`);
  }
}

function validatePreferredUnits(preferredUnits: string): void {
  if (preferredUnits !== 'imperial' && preferredUnits !== 'metric') {
    throw new Error(`Invalid preferred units: ${preferredUnits}. Must be 'imperial' or 'metric'`);
  }
}

function validateNotificationLeadDays(notificationLeadDays: number): void {
  if (!Number.isInteger(notificationLeadDays) || notificationLeadDays < 1 || notificationLeadDays > 30) {
    throw new Error('Notification lead days must be between 1 and 30');
  }
}

async function getCurrentUser(): Promise<User> {
  const userId = getCurrentUserId();
  const user = await db.getUserById(userId);
  if (!user) {
    throw new Error(`User not found: ${userId}`);
  }
  return user;
}

/**
 * Fetch the current user's billing tier from Supabase (server-authoritative),
 * write it back into Dexie, and return the refreshed record.
 * Falls back to the local Dexie record when offline or Supabase is unavailable.
 */
async function refreshTierFromRemote(): Promise<User> {
  const userId = getCurrentUserId();
  try {
    const remote = await fetchCurrentUserById(userId);
    if (remote) {
      // Merge billing fields from Supabase into the local record before persisting.
      // Non-billing fields use the local copy to avoid overwriting offline edits.
      const local = await db.getUserById(userId);
      if (local) {
        const merged: User = {
          ...local,
          tier: remote.tier,
          subscriptionStatus: remote.subscriptionStatus,
          stripeCustomerId: remote.stripeCustomerId,
          betaExpiresAt: remote.betaExpiresAt,
        };
        await db.updateUser(userId, {
          tier: merged.tier,
          subscriptionStatus: merged.subscriptionStatus,
          stripeCustomerId: merged.stripeCustomerId,
          betaExpiresAt: merged.betaExpiresAt,
        });
        return merged;
      } else {
        // No local record — create one from the remote row so subsequent calls succeed.
        // This can happen if a user authenticates on a new device before the sync
        // service has seeded the local Dexie store.
        // CreateUserInput excludes billing fields (ADR-032: server-authoritative on create);
        // dexie-adapter hard-sets tier:'starter', stripeCustomerId:null, subscriptionStatus:null.
        // Mirror the if-branch: immediately follow up with updateUser to carry billing fields.
        await db.createUser({
          id: remote.id,
          email: remote.email,
          displayName: remote.displayName,
          defaultState: remote.defaultState,
          preferredUnits: remote.preferredUnits,
        });
        return db.updateUser(remote.id, {
          tier: remote.tier,
          subscriptionStatus: remote.subscriptionStatus,
          stripeCustomerId: remote.stripeCustomerId,
          betaExpiresAt: remote.betaExpiresAt,
        });
      }
    }
  } catch (err) {
    // Network or Supabase error — fall back to local record silently
    console.warn('[user-service] refreshTierFromRemote: Supabase fetch failed, using local:', err);
  }
  return getCurrentUser();
}

async function updateProfile(data: UpdateUserInput): Promise<User> {
  const userId = getCurrentUserId();

  if (data.displayName !== undefined) {
    validateDisplayName(data.displayName);
  }
  if (data.defaultState !== undefined) {
    validateDefaultState(data.defaultState);
  }
  if (data.preferredUnits !== undefined) {
    validatePreferredUnits(data.preferredUnits);
  }

  return db.updateUser(userId, data);
}

async function getPreferences(): Promise<UserPreferences> {
  const userId = getCurrentUserId();
  const existing = await db.getUserPreferences(userId);
  if (existing) return existing;
  return db.updateUserPreferences(userId, {});
}

async function updatePreferences(data: UpdateUserPreferencesInput): Promise<UserPreferences> {
  const userId = getCurrentUserId();

  if (data.notificationLeadDays !== undefined) {
    validateNotificationLeadDays(data.notificationLeadDays);
  }

  return db.updateUserPreferences(userId, data);
}

export const userService = {
  getCurrentUser,
  refreshTierFromRemote,
  updateProfile,
  getPreferences,
  updatePreferences,
};
